import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const AUTOPLAY_MS = 6500;
const EASE = [0.22, 1, 0.36, 1];

/**
 * Cinematic full-bleed editorial slider.
 * — Full-width image beds into the aubergine section (no rectangular frame).
 * — Layered gradient + grain overlays blend the imagery with page texture.
 * — Editorial text overlays on the image itself (magazine spread style).
 * — Slow crossfade + subtle ken-burns between slides (not "slide-in").
 * — Vertical progress ticks + arrow chrome kept intentionally sparse.
 * — Autoplay pauses on hover; keyboard arrows navigate.
 */
export default function Slider({ eyebrow, heading, body, slides, id }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);

  // Parallax on scroll
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const yParallax = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  // Autoplay
  useEffect(() => {
    if (paused) return;
    const t = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      AUTOPLAY_MS,
    );
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const goto = useCallback(
    (i) =>
      setIndex(((i % slides.length) + slides.length) % slides.length),
    [slides.length],
  );
  const prev = () => goto(index - 1);
  const next = () => goto(index + 1);

  // Keyboard navigation when hovered
  useEffect(() => {
    if (!paused) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paused, index]);

  const active = slides[index];

  return (
    <section
      data-testid={`slider-${id}`}
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative bg-aubergine text-parchment overflow-hidden"
    >
      {/* ————— Full-bleed cinematic image stage ————— */}
      <div className="relative w-full h-[78vh] min-h-[560px] max-h-[820px] overflow-hidden">
        {/* Preload each image layer, only active is visible */}
        {slides.map((s, i) => (
          <AnimatePresence key={i}>
            {i === index && (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 1.8, ease: EASE }}
                className="absolute inset-0"
              >
                <motion.img
                  src={s.image}
                  alt={s.title}
                  className="absolute inset-0 h-[110%] w-full object-cover"
                  style={{ y: yParallax }}
                  draggable={false}
                />
              </motion.div>
            )}
          </AnimatePresence>
        ))}

        {/* Layered blending: bottom aubergine fade */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-aubergine via-aubergine/40 to-aubergine/20"
        />
        {/* Left column dark for legibility */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-aubergine/85 via-aubergine/20 to-transparent"
        />
        {/* Top light-touch gradient */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-aubergine/50 via-transparent to-transparent"
        />
        {/* Right accent glow */}
        <div
          aria-hidden
          className="absolute -right-40 top-1/4 h-96 w-96 rounded-full bg-magenta/15 blur-[120px]"
        />
        {/* Grain */}
        <div
          aria-hidden
          className="absolute inset-0 grain-overlay opacity-70 pointer-events-none"
        />

        {/* ————— Header (eyebrow + section headline) ————— */}
        <div className="absolute top-0 inset-x-0">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pt-14 md:pt-20">
            <div className="grid md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: EASE }}
                  className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4"
                >
                  <span className="h-px w-8 bg-magenta/70" />
                  {eyebrow}
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                  className="font-display font-light mt-5 md:mt-7 text-4xl md:text-5xl lg:text-6xl tracking-[-0.035em] leading-[0.98] text-parchment text-balance max-w-3xl"
                >
                  {heading}
                </motion.h2>
              </div>
              <div className="md:col-span-4 md:pt-8">
                <p className="font-serif italic text-parchment/75 leading-relaxed max-w-sm">
                  {body}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ————— Bottom-left editorial caption on-image ————— */}
        <div className="absolute bottom-0 inset-x-0">
          <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pb-14 md:pb-20">
            <div className="grid md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-8 relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
                    exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
                    transition={{ duration: 0.9, ease: EASE }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90 flex items-center gap-3">
                      <span className="font-display italic font-light text-2xl text-magenta not-uppercase tracking-normal leading-none">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="h-px w-6 bg-magenta/60" />
                      Chapter {String(index + 1).padStart(2, "0")} of{" "}
                      {String(slides.length).padStart(2, "0")}
                    </div>
                    <h3 className="mt-5 font-display font-light text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-[-0.045em] leading-[0.9] text-parchment text-balance max-w-3xl">
                      {active.title}
                    </h3>
                    <p className="mt-5 text-parchment/85 text-lg leading-relaxed max-w-xl">
                      {active.caption}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Vertical progress + arrows */}
              <div className="md:col-span-4 flex items-end justify-end gap-6">
                <div className="flex flex-col gap-3">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goto(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      data-testid={`${id}-slider-dot-${i}`}
                      className="group relative w-px h-14 md:h-16 bg-parchment/20 overflow-hidden"
                    >
                      <span
                        className={`absolute inset-0 origin-top transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          i === index
                            ? "bg-magenta scale-y-100"
                            : "scale-y-0 group-hover:scale-y-50 bg-parchment/50"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={prev}
                    aria-label="Previous slide"
                    data-testid={`${id}-slider-prev`}
                    className="group h-12 w-12 grid place-items-center border border-parchment/30 hover:border-magenta hover:bg-magenta transition-all duration-300"
                  >
                    <ArrowLeft className="h-4 w-4 text-parchment/85 group-hover:text-parchment transition-colors" />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next slide"
                    data-testid={`${id}-slider-next`}
                    className="group h-12 w-12 grid place-items-center border border-parchment/30 hover:border-magenta hover:bg-magenta transition-all duration-300"
                  >
                    <ArrowRight className="h-4 w-4 text-parchment/85 group-hover:text-parchment transition-colors" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Autoplay progress bar (bottom edge) */}
        <div className="absolute bottom-0 inset-x-0 h-[2px] bg-parchment/10">
          <motion.div
            key={`${index}-${paused}`}
            className="h-full bg-magenta origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: paused ? 0.35 : 1 }}
            transition={{
              duration: paused ? 0 : AUTOPLAY_MS / 1000,
              ease: "linear",
            }}
          />
        </div>
      </div>
    </section>
  );
}
